import React, { useCallback, useState } from "react";
import styled from "@emotion/styled";

import { Card, CardHeader } from "../Card/Card";
import { Button } from "../Button";
import { Banner, BannerTypes } from "../Banner";
import { Typography } from "../Typography";
import { modalDefaults } from "./modals";
import { ContactCardModal } from "./ContactCardModal";
import { Col, Row, UnderlinedButtonStyle } from "./shared";
import { CONTACT_TYPES_TEXT, TEST_ID_PREFIX } from "./constants";
import { ContactListEdit } from "./ContactListEdit";
import type { Contact, ContactCardProps } from "./types";
import { CreditStatus } from "./CreditStatus";
import { useUpdateUser } from "./useUpdateUser";

const ContactCardCheckout = ({
  contactType,
  contactList,
  selectedContactId,
  hasOffer,
  showCreditStatus,
  disabled,
  disableSameSignerValidation,
  disableEditEmail,
  customApprovedStatusText,
  customNotApprovedStatusText,
  customNotCheckedStatusText,
  toolTipText,
  initialEditState = false,
  onSelectionUpdate: updateSelectedId,
  onContactInfoSave: saveContact,
  onRunCreditCheck,
  onRefreshCreditCheck,
  cardTitleText,
  secondaryButtonText,
  customAddNewText,
  modals = modalDefaults,
}: ContactCardProps) => {
  const [newContactId, setNewContactId] = useState("");
  const [isEditing, setIsEditing] = useState(initialEditState);

  const selectedContact = contactList.find(
    ({ contactId }) => contactId === selectedContactId
  );

  const hasContactInfo = (contact: Contact) =>
    !!(contact?.customerPrimaryPhone?.length && contact?.customerEmail?.length);

  const select = useCallback(
    async (overrideContactId?: string) => {
      try {
        await updateSelectedId(overrideContactId ?? newContactId);
        setIsEditing(false);
      } catch (ex) {
        // Stay expanded because save attempt was rejected
      }
    },
    [newContactId, updateSelectedId]
  );

  const { updateUser, currentModal, closeModal } = useUpdateUser({
    select,
    contactType,
    hasOffer,
    contactList,
    disableSameSignerValidation,
    modals,
  });

  const handleSaveContact = async (newContact: Contact) => {
    const savedContact = await saveContact(newContact);

    // NOTE: if selected contact is changed, go to non-edit mode
    if (selectedContactId === newContact?.contactId && !!selectedContactId) {
      setIsEditing(false);
    }
    return savedContact;
  };

  const handleUpdateSelectedId = async (newestContactId: string) => {
    if (newestContactId === selectedContact?.contactId) {
      closeModal();
      return setIsEditing(false);
    }

    updateUser(newestContactId);

    setNewContactId(newestContactId);
  };

  // Early exit if it's an unset secondary
  if (contactType === "secondary" && !selectedContact && !isEditing) {
    return (
      <Button
        color="secondary"
        onClick={() => setIsEditing(true)}
        data-testid="secondary-signer-select-add"
      >
        Add Secondary Signer
      </Button>
    );
  }

  return (
    <CardStyled dataTestId={`${TEST_ID_PREFIX[contactType]}-card-container`}>
      <CardHeader
        contactType={contactType}
        toolTipText={toolTipText}
        title={cardTitleText ?? CONTACT_TYPES_TEXT[contactType]}
        rightContent={
          <>
            {contactType === "secondary" && (
              <UnderlinedButtonStyle
                size="xs"
                color="tertiary"
                onClick={() => handleUpdateSelectedId("")}
                style={{ visibility: !isEditing ? "visible" : "hidden" }}
                data-testid={`${TEST_ID_PREFIX[contactType]}-contact-remove`}
              >
                Remove
              </UnderlinedButtonStyle>
            )}
            <UnderlinedButtonStyle
              size="xs"
              color="tertiary"
              onClick={() => setIsEditing(true)}
              style={{ visibility: !isEditing ? "visible" : "hidden" }}
              data-testid={`${TEST_ID_PREFIX[contactType]}-contact-edit`}
            >
              Edit
            </UnderlinedButtonStyle>
          </>
        }
      />

      {isEditing ? (
        <ContactListEdit
          contactType={contactType}
          contactList={contactList}
          selectedContactId={selectedContactId}
          disableSameSignerValidation={disableSameSignerValidation}
          disabled={disabled}
          hasOffer={hasOffer}
          onCancel={() => setIsEditing(false)}
          updateSelectedId={handleUpdateSelectedId}
          saveContact={handleSaveContact}
          customApprovedStatusText={customApprovedStatusText}
          customNotApprovedStatusText={customNotApprovedStatusText}
          customNotCheckedStatusText={customNotCheckedStatusText}
          disableEditEmail={disableEditEmail}
          customAddNewText={customAddNewText}
          secondaryButtonText={secondaryButtonText}
        />
      ) : (
        <ContactCardContainer data-testid="contact-card-info">
          {selectedContactId && !hasContactInfo(selectedContact) && (
            <Banner
              show
              bannerTestId="noContactInfoBanner"
              bannerType={BannerTypes.ErrorLight}
              showClose={false}
            >
              You need to fill in the contact information to continue.
            </Banner>
          )}
          <InfoContainer>
            <Col>
              <Typography role="secondary" as="label">
                Name
              </Typography>
              <Typography>
                {selectedContact?.customerFirstName ?? ""}{" "}
                {selectedContact?.customerLastName ?? ""}
              </Typography>
            </Col>
            <PhoneContainer>
              <Typography role="secondary" as="label">
                Phone
              </Typography>
              <Typography>
                {selectedContact?.customerPrimaryPhone ?? ""}
              </Typography>
            </PhoneContainer>
            <EmailContainer>
              <Typography role="secondary" as="label">
                Email
              </Typography>
              <Typography>{selectedContact?.customerEmail ?? "-"}</Typography>
            </EmailContainer>
          </InfoContainer>
          {selectedContact?.creditStatus &&
            showCreditStatus &&
            ["primary", "secondary"].includes(contactType) && (
              <CreditStatus
                onRefreshCreditCheck={onRefreshCreditCheck}
                onRunCreditCheck={onRunCreditCheck}
                contact={selectedContact}
                creditStatus={selectedContact?.creditStatus}
                text={{
                  approved: customApprovedStatusText,
                  notApproved: customNotApprovedStatusText,
                  notChecked: customNotCheckedStatusText,
                }}
              />
            )}
        </ContactCardContainer>
      )}

      {currentModal && (
        <ContactCardModal
          modal={currentModal}
          contactType={contactType}
          onClose={closeModal}
        />
      )}
    </CardStyled>
  );
};

const CardStyled = styled(Card)`
  .modal-wrapper {
    padding: 0;
    margin: 0;
  }
`;

const ContactCardContainer = styled(Col)`
  gap: 24px;
`;

const PhoneContainer = styled(Col)`
  margin-left: auto;
`;

const EmailContainer = styled(Col)`
  margin-left: auto;
  margin-right: auto;
`;

const InfoContainer = styled(Row)`
  max-width: 888px;
`;

export { ContactCardCheckout };
